import { useState, useCallback } from 'react';
import { ScanSearch, AlertTriangle, CheckCircle2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { FieldHint } from '@/components/shared/FieldHint';
import { useRetroactiveRegister } from '@/hooks/useConnectors';
import type { RetroactiveRegisterResponse } from '@/types/connector';

const DEFAULT_SCAN_LIMIT = 1000;

interface RegisterVectorsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  connectorId: string;
  connectorName: string;
}

export function RegisterVectorsDialog({
  open,
  onOpenChange,
  connectorId,
  connectorName,
}: RegisterVectorsDialogProps) {
  const [scanLimit, setScanLimit] = useState(String(DEFAULT_SCAN_LIMIT));
  const [defaultClassification, setDefaultClassification] = useState('');
  const [defaultSensitivity, setDefaultSensitivity] = useState('');
  const [defaultDomain, setDefaultDomain] = useState('');
  // First action defaults to a dry run so the user can preview counts before
  // committing any writes.
  const [dryRun, setDryRun] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<RetroactiveRegisterResponse | null>(null);
  const registerMutation = useRetroactiveRegister();

  const reset = useCallback(() => {
    setScanLimit(String(DEFAULT_SCAN_LIMIT));
    setDefaultClassification('');
    setDefaultSensitivity('');
    setDefaultDomain('');
    setDryRun(true);
    setError(null);
    setResult(null);
  }, []);

  const handleRun = useCallback(() => {
    setError(null);
    const parsedLimit = parseInt(scanLimit, 10);
    if (!Number.isFinite(parsedLimit) || parsedLimit < 1 || parsedLimit > 10000) {
      setError('Scan limit must be between 1 and 10,000.');
      return;
    }

    registerMutation.mutate(
      {
        connector_id: connectorId,
        scan_limit: parsedLimit,
        default_classification: defaultClassification.trim() || undefined,
        default_sensitivity: defaultSensitivity.trim() || undefined,
        default_domain: defaultDomain.trim() || undefined,
        dry_run: dryRun,
      },
      {
        onSuccess: (data) => setResult(data),
        onError: (err) => setError(err instanceof Error ? err.message : 'Registration failed'),
      },
    );
  }, [connectorId, scanLimit, defaultClassification, defaultSensitivity, defaultDomain, dryRun, registerMutation]);

  function handleClose(o: boolean) {
    if (!o) reset();
    onOpenChange(o);
  }

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Register Existing Vectors: {connectorName}</DialogTitle>
          <DialogDescription>
            Your database already has vectors, but Gateco does not govern them until they are
            registered as resources. This scans the connector and registers what it finds. Run a
            dry run first to preview counts.
          </DialogDescription>
        </DialogHeader>

        {result ? (
          <div className="space-y-4">
            <div className={`flex items-center gap-2 ${result.status === 'dry_run' ? 'text-status-info' : 'text-status-allow'}`}>
              <CheckCircle2 className="h-5 w-5" />
              <span className="font-semibold">
                {result.status === 'dry_run' ? 'Dry Run Complete' : 'Registration Complete'}
              </span>
            </div>
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div className="rounded bg-muted/50 p-3">
                <p className="text-muted-foreground text-xs">Scanned Vectors</p>
                <p className="font-bold text-lg">{result.scanned_vectors}</p>
              </div>
              <div className="rounded bg-muted/50 p-3">
                <p className="text-muted-foreground text-xs">Already Registered</p>
                <p className="font-bold text-lg">{result.already_registered}</p>
              </div>
              <div className="rounded bg-muted/50 p-3">
                <p className="text-muted-foreground text-xs">
                  {result.status === 'dry_run' ? 'Would Register' : 'Newly Registered'}
                </p>
                <p className="font-bold text-lg">{result.newly_registered}</p>
              </div>
              <div className="rounded bg-muted/50 p-3">
                <p className="text-muted-foreground text-xs">
                  {result.status === 'dry_run' ? 'Would Create Resources' : 'Resources Created'}
                </p>
                <p className="font-bold text-lg">{result.resources_created}</p>
              </div>
            </div>
            {result.status === 'dry_run' && (
              <FieldHint tone="muted">
                This was a preview only. No resources were created. Uncheck &ldquo;Dry run&rdquo;
                and run again to register these vectors.
              </FieldHint>
            )}
            {result.errors.length > 0 && (
              <div className="space-y-1">
                <p className="text-sm font-medium text-destructive">{result.errors.length} error(s):</p>
                <div className="max-h-32 overflow-y-auto text-xs space-y-0.5">
                  {result.errors.map((err, i) => (
                    <p key={i} className="text-destructive">
                      {JSON.stringify(err)}
                    </p>
                  ))}
                </div>
              </div>
            )}
            <DialogFooter>
              {result.status === 'dry_run' ? (
                <>
                  <Button variant="outline" onClick={() => setResult(null)}>Back</Button>
                  <Button
                    onClick={() => {
                      setDryRun(false);
                      setResult(null);
                    }}
                  >
                    Run for Real
                  </Button>
                </>
              ) : (
                <Button onClick={() => { reset(); onOpenChange(false); }}>Done</Button>
              )}
            </DialogFooter>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="space-y-1">
              <label className="block text-sm font-medium">Scan limit</label>
              <Input
                type="number"
                min={1}
                max={10000}
                value={scanLimit}
                onChange={(e) => setScanLimit(e.target.value)}
              />
              <FieldHint>Maximum number of vectors to scan in this pass (1 to 10,000).</FieldHint>
            </div>

            <div className="space-y-1">
              <label className="block text-sm font-medium">Default classification (optional)</label>
              <select
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                value={defaultClassification}
                onChange={(e) => setDefaultClassification(e.target.value)}
              >
                <option value="">— none —</option>
                <option value="public">Public</option>
                <option value="internal">Internal</option>
                <option value="confidential">Confidential</option>
                <option value="restricted">Restricted</option>
              </select>
            </div>

            <div className="space-y-1">
              <label className="block text-sm font-medium">Default sensitivity (optional)</label>
              <select
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                value={defaultSensitivity}
                onChange={(e) => setDefaultSensitivity(e.target.value)}
              >
                <option value="">— none —</option>
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
                <option value="critical">Critical</option>
              </select>
            </div>

            <div className="space-y-1">
              <label className="block text-sm font-medium">Default domain (optional)</label>
              <Input
                placeholder="e.g. engineering"
                value={defaultDomain}
                onChange={(e) => setDefaultDomain(e.target.value)}
              />
            </div>

            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={dryRun}
                onChange={(e) => setDryRun(e.target.checked)}
                className="h-3.5 w-3.5 rounded border-input accent-primary"
              />
              <span className="text-sm">Dry run (preview only, no writes)</span>
            </label>

            {error && (
              <p className="text-sm text-destructive bg-destructive/10 rounded p-2 flex items-start gap-1">
                <AlertTriangle className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />{error}
              </p>
            )}

            <DialogFooter>
              <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
              <Button onClick={handleRun} disabled={registerMutation.isPending}>
                <ScanSearch className="h-4 w-4 mr-2" />
                {registerMutation.isPending ? 'Scanning...' : dryRun ? 'Preview' : 'Register'}
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
